import Element from '../../domain/Element/Element';
import { IControlledElement } from "../../domain/Element/ElementInterface";

export function applyControlledElementsActions(
  controlledElements: IControlledElement[],
  parentElementValue: string|number|boolean|null,
  allElements: Element[]
): Element[] {
  if (controlledElements.length === 0) {
    return []
  }

  const modifiedElements: Element[] = [];

  controlledElements.forEach((controlledElement) => {
    const targetElement = allElements.find(el => el.name === controlledElement.element_name)

    if (!targetElement) {
        return;
    }

    const matchesScalar = (expected: unknown, actual: unknown): boolean => {
      if (expected === actual) return true
      if (actual === null || actual === undefined) return false
      // After a backend round-trip booleans come back as strings ("true"/"false"),
      // while controlled_elements.on_parent_value stays a JSONB boolean. Tolerate
      // this by comparing string forms so the override still applies on remount.
      return String(expected) === String(actual)
    }

    const shouldApplyActions = Array.isArray(controlledElement.on_parent_value)
      ? parentElementValue !== null && parentElementValue !== undefined
        && (controlledElement.on_parent_value as Array<unknown>).some(v => matchesScalar(v, parentElementValue))
      : matchesScalar(controlledElement.on_parent_value, parentElementValue)

    // Create a deep copy to ensure Redux detects changes
    const updatedElement: Element = JSON.parse(JSON.stringify(targetElement))

    // Toggle apply attributes
    if (shouldApplyActions) {
      // Apply new attributes
      Object.keys(controlledElement.attributes).forEach((attributeKey) => {
        if (attributeKey === 'css_class') {
          const cssClasses = controlledElement.attributes[attributeKey]
          if (cssClasses && !updatedElement.cssClasses.includes(cssClasses)) {
            updatedElement.cssClasses = `${updatedElement.cssClasses} ${cssClasses}`.trim()
          }

          return;
        }

        if ((updatedElement as any)._originalAttributes === undefined) {
          (updatedElement as any)._originalAttributes = {}
        }
        const originalAttributes = (updatedElement as any)._originalAttributes
        if (!(attributeKey in originalAttributes)) {
          originalAttributes[attributeKey] = updatedElement.attributes[attributeKey]
        }

        updatedElement.attributes[attributeKey] = controlledElement.attributes[attributeKey];

        // Override condition.actions.required/disabled so the condition does not
        // re-impose the original behaviour over our attribute override.
        if (
          (attributeKey === 'required' || attributeKey === 'disabled')
          && updatedElement.condition?.actions
        ) {
          const backupKey = `_originalCondition${attributeKey === 'required' ? 'Required' : 'Disabled'}`
          if ((updatedElement as any)[backupKey] === undefined) {
            (updatedElement as any)[backupKey] = updatedElement.condition.actions[attributeKey]
          }
          updatedElement.condition.actions[attributeKey] = controlledElement.attributes[attributeKey]
        }

        // Specific case for calculated fields: ignore calculation when readonly is false
        if (attributeKey === 'readonly' && updatedElement.calculate && controlledElement.attributes?.readonly === false) {
          updatedElement.ignoreCalculate = true;
        }
      });
    } else {
      // Reset attributes
      Object.keys(controlledElement.attributes).forEach((attributeKey) => {
        if (attributeKey === 'css_class') {
          const cssClasses = controlledElement.attributes[attributeKey];
          if (cssClasses) {
            updatedElement.cssClasses = updatedElement.cssClasses
              .split(' ')
              .filter(cls => cls !== cssClasses)
              .join(' ')
              .trim();
          }
          return;
        }

        const originalAttributes = (updatedElement as any)._originalAttributes
        if (originalAttributes && attributeKey in originalAttributes) {
          updatedElement.attributes[attributeKey] = originalAttributes[attributeKey]
          delete originalAttributes[attributeKey]
        }

        // Restore the original condition.actions value saved when the toggle was activated.
        if (
          (attributeKey === 'required' || attributeKey === 'disabled')
          && updatedElement.condition?.actions
        ) {
          const backupKey = `_originalCondition${attributeKey === 'required' ? 'Required' : 'Disabled'}`
          if ((updatedElement as any)[backupKey] !== undefined) {
            updatedElement.condition.actions[attributeKey] = (updatedElement as any)[backupKey]
            delete (updatedElement as any)[backupKey]
          }
        }

        if (attributeKey === 'readonly' && updatedElement.calculate) {
          delete updatedElement.ignoreCalculate;
        }
      });
    }

    modifiedElements.push(updatedElement)
  })

  return modifiedElements
}
